import React, { useEffect, useState } from 'react';
import { HashRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { DatabaseProvider } from './contexts/DatabaseContext';
import { OfflineProvider } from './contexts/OfflineContext';
import { TenantProvider } from './contexts/TenantContext';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Login from './pages/auth/Login';
import Signup from './pages/auth/Signup';
import SignupSuccess from './pages/auth/SignupSuccess';
import Dashboard from './pages/Dashboard';
import FormRouter from './components/forms/FormRouter';
import FormLibrary from './pages/FormLibrary';
import Customers from './pages/Customers';
import Properties from './pages/Properties';
import Templates from './pages/Templates';
import Backup from './pages/Backup';
import AdminSettings from './pages/AdminSettings';
import Debug from './pages/Debug';
import InstallPrompt from './lib/pwa/InstallPrompt';
import AppLayout from './components/layout/AppLayout';
import CommandPalette from './components/common/CommandPalette';
import useKeyboardShortcuts from './hooks/useKeyboardShortcuts';
import './styles/globals.css';

/**
 * Authenticated workspace shell: nav rail, header, command palette and all
 * gated application routes. Rendered only when ProtectedRoute confirms a
 * valid PocketBase session.
 */
function AppWorkspace() {
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);
  const navigate = useNavigate();

  // Register global shortcuts
  useKeyboardShortcuts({
    onOpenCommandPalette: () => setIsCommandPaletteOpen((prev) => !prev),
    onEscape: () => setIsCommandPaletteOpen(false),
  });

  return (
    <AppLayout
      onOpenCommandPalette={() => setIsCommandPaletteOpen(true)}
      onOpenNewCertificate={() => navigate('/forms')}
      pendingSyncCount={0}
    >
      <InstallPrompt />
      <CommandPalette
        isOpen={isCommandPaletteOpen}
        onClose={() => setIsCommandPaletteOpen(false)}
      />

      <Routes>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/customers" element={<Customers />} />
        <Route path="/properties" element={<Properties />} />
        <Route path="/forms" element={<FormLibrary />} />
        <Route path="/forms/:formId" element={<FormRouter />} />
        <Route path="/forms/:formId/:formInstanceId" element={<FormRouter />} />
        <Route path="/templates" element={<Templates />} />
        <Route path="/backup" element={<Backup />} />
        <Route path="/settings" element={<AdminSettings />} />
        <Route path="/debug" element={<Debug />} />
        <Route
          path="*"
          element={
            <div className="p-12 text-center text-slate-500">
              <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-2">404 - Page Not Found</h2>
              <p className="text-sm">The requested view does not exist.</p>
              <button
                onClick={() => navigate('/dashboard')}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg text-xs font-semibold"
              >
                Return to Dashboard
              </button>
            </div>
          }
        />
      </Routes>
    </AppLayout>
  );
}

function AppShell() {
  return (
    <Routes>
      {/* Public authentication routes (no workspace chrome) */}
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      {/* Post-Stripe-checkout return page — polls for the webhook-created
          account, then runs the OTP verification (docs/signup.md §3). */}
      <Route path="/signup/success" element={<SignupSuccess />} />
      {/* /verify-otp resumes the Login OTP step when an otpId is present in state */}
      <Route path="/verify-otp" element={<Login />} />

      {/* Everything else requires an authenticated session */}
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <AppWorkspace />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    // Register service worker for PWA in production builds only
    if (import.meta.env.PROD && 'serviceWorker' in navigator) {
      navigator.serviceWorker
        .register('/sw.js')
        .then((registration) => {
          console.log('✅ Service Worker registered:', registration);
        })
        .catch((registrationError) => {
          console.log('❌ Service Worker registration failed:', registrationError);
        });
    }
  }, []);

  return (
    <ThemeProvider>
      <TenantProvider>
        <AuthProvider>
          <DatabaseProvider>
            <OfflineProvider>
              <HashRouter>
                <AppShell />
              </HashRouter>
            </OfflineProvider>
          </DatabaseProvider>
        </AuthProvider>
      </TenantProvider>
    </ThemeProvider>
  );
}

export default App;
